import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IMeasurementTypeTemplateItem,
  IWardMeasurementType,
} from "../../../@types/admin/AdminMeasurementType";
import { TableSorted } from "../../../components/TableSorted";
import { useApplyMeasurementTypeTemplate } from "../../../hooks/mutations/admin/measurementTypes";
import { useMeasurementTypeTemplates } from "../../../hooks/queries/admin/measurementTypes";
import { useToast } from "../../../hooks/useToast";
import {
  errorToastMessage,
  serverErrorMessage,
} from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  TableContainer,
  TableWrapper,
  WarningNotice,
} from "../shared";
import { ModalBodyText } from "./AdminWards.styles";

interface IApplyMeasurementTypeTemplateModalProps {
  wardId: number;
  wardName: string;
  /** The ward's current configuration, for the replace/merge preview. */
  currentTypes: IWardMeasurementType[];
  onClose: () => void;
}

/**
 * Apply a measurement-type template to a ward — Retool's `modalTemplates`
 * (EN-5157).
 *
 * Retool put an Overwrite switch in the modal footer with the caption "This
 * will replace all measurement types" and applied on one click. That is the
 * most destructive action on the page — overwrite drops every code the ward
 * has that the template doesn't — so here the template's contents are shown
 * before applying, and overwrite spells out how many rows it would remove.
 */
export function ApplyMeasurementTypeTemplateModal({
  wardId,
  wardName,
  currentTypes,
  onClose,
}: IApplyMeasurementTypeTemplateModalProps) {
  const { showToast } = useToast();
  const { templates, isLoading, isError } = useMeasurementTypeTemplates();
  const { applyMeasurementTypeTemplate, isApplying } =
    useApplyMeasurementTypeTemplate(wardId);

  const [templateId, setTemplateId] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  /** The server's reason for refusing — see the note in the edit modal. */
  const [rejection, setRejection] = useState<string | undefined>();

  const template = templates.find((item) => item.id === templateId);

  /**
   * Codes the ward has that the template doesn't. With overwrite on, these
   * are what an Apply would delete — the number Retool never showed.
   */
  const droppedCodes = useMemo(() => {
    if (!template) return [];
    const templateCodes = new Set(
      template.items.map((item) => item.measurementTypeCode)
    );
    return currentTypes.filter(
      (type) => !templateCodes.has(type.measurementTypeCode)
    );
  }, [template, currentTypes]);

  const handleApply = async () => {
    setRejection(undefined);
    if (!template) return;

    try {
      await applyMeasurementTypeTemplate({
        templateId: template.id,
        overwrite,
      });
      showToast("The template was successfully applied.", "success");
      onClose();
    } catch (error) {
      // Overwriting can drop or hide a type another feature depends on, so
      // this rejects the same way a single edit does.
      const refused = serverErrorMessage(error);
      if (refused) {
        setRejection(refused);
        return;
      }
      showToast(
        errorToastMessage(error, "There was an error applying this template."),
        "warning"
      );
    }
  };

  const columns: ColumnDef<IMeasurementTypeTemplateItem, string>[] = useMemo(
    () => [
      {
        accessorKey: "measurementTypeDisplayName",
        header: "Measurement type",
        enableSorting: true,
      },
      {
        accessorKey: "measurementTypeCode",
        header: "Type code",
        enableSorting: true,
      },
      {
        id: "displayInPortal",
        header: "Portal",
        accessorFn: (item) => (item.displayInPortal ? "Yes" : "No"),
        enableSorting: true,
      },
      {
        id: "displayInApp",
        header: "App",
        accessorFn: (item) => (item.displayInApp ? "Yes" : "No"),
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <ModalContainer>
      <ModalTitle>Apply template</ModalTitle>

      <ModalForm>
        {rejection && <WarningNotice>{rejection}</WarningNotice>}

        <ModalBodyText>
          Apply a measurement-type template to <strong>{wardName}</strong>.
        </ModalBodyText>

        {isError && (
          <WarningNotice>
            The templates could not be loaded. Close the dialog and try again.
          </WarningNotice>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="measurement-type-template">
            Template
          </FilterLabel>
          <AdminSelectField
            id="measurement-type-template"
            value={templateId}
            disabled={isLoading}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">
              {isLoading ? "Loading..." : "Select an option"}
            </option>
            {templates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </AdminSelectField>
          {template?.description && (
            <FieldHint>{template.description}</FieldHint>
          )}
        </FilterGroup>

        {template && (
          <TableContainer>
            <TableWrapper refreshKeys={[["admin-measurement-type-templates"]]}>
              <TableSorted
                columns={columns}
                data={template.items}
                emptyStateText="This template has no measurement types"
              />
            </TableWrapper>
          </TableContainer>
        )}

        <FilterGroup>
          <FilterLabel>Existing configuration</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
            />
            Replace the ward&apos;s measurement types
          </CheckboxField>
          <FieldHint>
            {overwrite
              ? "The template becomes the ward's whole configuration."
              : "The template is merged in: existing types are kept, and the template wins where they overlap."}
          </FieldHint>
        </FilterGroup>

        {overwrite && droppedCodes.length > 0 && (
          <WarningNotice>
            This removes {droppedCodes.length}{" "}
            {droppedCodes.length === 1
              ? "measurement type"
              : "measurement types"}{" "}
            the ward has and the template does not:{" "}
            {droppedCodes
              .map((type) => type.measurementTypeDisplayName)
              .join(", ")}
            .
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isApplying}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isApplying || !template}
            onClick={handleApply}
          >
            {isApplying ? "Applying..." : "Apply"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
